import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { LoginService } from 'src/app/core/services/login/login.service';

import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { EventTypes } from 'src/app/core/enums/event.enum';
import { SignInStatus } from 'src/app/core/enums/sign-in-status.enum';
import { ILogin } from 'src/app/core/interfaces/login/login.interface';
import { PageState } from 'src/app/core/models/page-state/page-state.model';
import { HydrationService } from 'src/app/core/services/login/hydration.service';
import { EnrollmentService } from 'src/app/core/services/onboarding/enrollment.service';
import { LegacyUpgradeService } from 'src/app/core/services/onboarding/legacy-upgrade.service';
import { PageStateService } from 'src/app/core/services/onboarding/page-state.service';
import { ServersideAlertService } from 'src/app/core/services/onboarding/serverside-alert.service';
import { ClearAction } from 'src/app/core/store/common/actions/clear.action';
import { IAppState } from 'src/app/core/store/common/interfaces/app-state.interface';
import {
  HydrationRoles,
  HydrationSources,
} from 'src/app/core/store/common/interfaces/hydration-status-store.interface';
import { destructSubscriptionArray } from 'src/app/core/ui-helpers/functions/destruct-subscription-array';
import { redirectToPreDashboard } from 'src/app/core/ui-helpers/functions/redirect/redirect-to-predashboard';
import { reloadApp } from 'src/app/core/ui-helpers/functions/redirect/reload-app';
import { PrimaryButtonComponent } from 'src/app/shared/common/primary-button/primary-button.component';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
  private apiUrl = environment.apiUrl;
  subscriptions: Subscription[] = [];
  form: FormGroup;
  email: string;
  password: string;
  signupState = new PageState();
  nextText = 'Sign In';
  toolTipText =
    'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Fusce vel arcu eget mauris ullamcorper vestibulum. Quisque bibendum mi in semper consectetur. Ut non mattis ipsum pharetra.';
  isLoad: boolean;

  // Form Labels
  signUpText = 'Sign Up';
  emailText = 'Email';
  passwordText = 'Password';
  signInBtnText = 'Sign In';
  intuitBtnText = ' Sign in with Intuit';

  @ViewChild(PrimaryButtonComponent) submitButton: PrimaryButtonComponent;

  constructor(
    private lg: LoginService,
    private route: ActivatedRoute,
    private ps: PageStateService,
    private fb: FormBuilder,
    private httpClient: HttpClient,
    private router: Router,
    private ssas: ServersideAlertService,
    private hs: HydrationService,
    private store: Store<IAppState>,
    private lus: LegacyUpgradeService,
    private es: EnrollmentService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.store.dispatch(ClearAction());
    this.subscriptions.push(
      this.route.data.subscribe(async (data) => {
        this.signupState = await this.ps.getPageStateByEventAndTransitions(
          EventTypes.SIGN_UP,
          data['data'],
        );
      }),
    );

    this.form = this.fb.group({
      email: ['', [Validators['required'], Validators.email]],
      password: ['', [Validators.required]],
    });
  }

  ngOnDestroy(): void {
    destructSubscriptionArray(this.subscriptions);
  }

  async submitForm() {
    if (this.form.valid) {
      const payload: ILogin = {
        Email: this.form.get('email')?.value ?? '',
        Password: this.form.get('password')?.value ?? '',
        RememberMe: true,
      };

      const result = await this.lg.userlogin(payload);

      if (result.IsPhoneNumberRequired === true) {
        this.redirectToSetPhoneNumber();
        this.isLoad = true;
      }
      if (result.SignInStatus === SignInStatus.Success) {
        if (result.IsLegacyUpgrade) {
          const confirmedLegacy = await this.lus.redirectToLegacyUpgrade();
          if (confirmedLegacy) {
            const predashboardUrl = this.router
              .createUrlTree(['OnBoarding/OnBoarding/PreDashBoard'])
              .toString();
            this.router.navigate([predashboardUrl]);
            this.isLoad = true;
          }
        } else {
          if (
            result.LoggedInRole === HydrationRoles.onboardingProspect ||
            result.LoggedInRole === HydrationRoles.onboardingClient ||
            result.LoggedInRole ===
              HydrationRoles.onboardingProspectOrOnboardingClient
          ) {
            await this.hs.HydrateOnboardingStore(
              result.LoggedInRole,
              HydrationSources.login,
            );
            redirectToPreDashboard(this.router);
          } else if (
            result.LoggedInRole === HydrationRoles.activeClient ||
            result.LoggedInRole === HydrationRoles.internal
          ) {
            await this.hs.HydrateAccountStore(
              result.LoggedInRole,
              HydrationSources.login,
            );
            reloadApp(environment);
          }
        }
        //this.autoLogin(payload);
        this.isLoad = true;
      }
      if (result.SignInStatus === SignInStatus.RequiresVerification) {
        this.redirectToVerifyUser();
        this.isLoad = true;
      }
      if (result.SignInStatus === SignInStatus.Failure) {
        this.ssas.reportAlert([result.Message], 'warn');
        this.isLoad = false;
      }
      if (result.SignInStatus === SignInStatus.LockedOut) {
        this.ssas.reportAlert([result.Message], 'warn');
        this.isLoad = false;
      }
      if (result.Success === false) {
        this.ssas.reportAlert([result.Message], 'warn');
        this.isLoad = false;
      }
    } else {
      // mark all controls as touched to trigger validation
      this.form.markAllAsTouched();
      this.isLoad = false;
    }
    return this.isLoad;
  }

  // if user ip is same as last it auto login
  async autoLogin(payload: ILogin) {
    this.store.dispatch(ClearAction());
    this.subscriptions.push(
      this.lg.autoLogin(payload).subscribe(async (res: any) => {
        if (res.SignInStatus === SignInStatus.Success) {
          reloadApp(environment);
        }
        if (res.SignInStatus === SignInStatus.RequiresVerification) {
          this.redirectToVerifyUser();
        }
        if (
          res.SignInStatus === SignInStatus.Failure ||
          res.SignInStatus === SignInStatus.LockedOut
        ) {
          this.redirectToLogin();
        }
      }),
    );
  }

  // sign-in with quickbook
  async signInWithIntuit() {
    this.subscriptions.push(
      this.lg.signInWithIntuit().subscribe((res: any) => {
        this.redirectToIntuit(res);
      }),
    );
    return true;
  }

  redirectToVerifyUser() {
    this.router.navigate(['/verifyuser']);
  }

  redirectToLogin() {
    this.router.navigate(['/login']);
  }

  redirectToIntuit(url: string) {
    window.location.href = url;
  }

  redirectToSetPhoneNumber() {
    this.router.navigate(['/set-phonenumber']);
  }

  onEnter() {
    this.submitButton.submit('');
  }
}
